import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

import { getActionsForRole, getRolesForModule, getModuleLabel } from '../lib/vcm/role-matrix';
import type { VcmModule, ActionId } from '../types/vcm';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface VcmEvent {
  name: string;
  programCode: string;
  isActive: boolean;
}

/* MOCK DATA — replace with real event data from /v2 API */
const PRIMARY_EVENT: VcmEvent = {
  name: 'SSVF – Austin Area',
  programCode: 'SSVF-TX-2026-001',
  isActive: true,
};

export const AVAILABLE_EVENTS: VcmEvent[] = [
  PRIMARY_EVENT,
  { name: 'Veterans Housing Assistance – South TX', programCode: 'VHAP-TX-2026-001', isActive: true },
];

export interface VcmContextValue {
  module: VcmModule;
  role: string;
  moduleLabel: string;
  availableRoles: string[];
  availableActions: ActionId[];
  setModule: (m: VcmModule) => void;
  setRole: (r: string) => void;
  currentEvent: VcmEvent;
  availableEvents: VcmEvent[];
  setCurrentEvent: (event: VcmEvent) => void;
  saveStatus: SaveStatus;
  setSaveStatus: (s: SaveStatus) => void;
  currentUser: { name: string; initials: string; role: string };
}

const VcmContext = createContext<VcmContextValue | null>(null);

export function VcmProvider({ children }: { readonly children: ReactNode }) {
  const [module, setModuleState] = useState<VcmModule>('dcm');
  const [role, setRole] = useState<string>('Case Manager');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [currentEvent, setCurrentEvent] = useState<VcmEvent>(PRIMARY_EVENT);

  const setModule = useCallback((m: VcmModule) => {
    setModuleState(m);
    const roles = getRolesForModule(m);
    setRole(roles[0] ?? '');
  }, []);

  const value = useMemo<VcmContextValue>(() => {
    const availableRoles = getRolesForModule(module);
    const availableActions = getActionsForRole(module, role);
    const moduleLabel = getModuleLabel(module);
    return {
      module,
      role,
      moduleLabel,
      availableRoles,
      availableActions,
      setModule,
      setRole,
      currentEvent,
      availableEvents: AVAILABLE_EVENTS,
      setCurrentEvent,
      saveStatus,
      setSaveStatus,
      currentUser: { name: 'Noah Golding', initials: 'NG', role },
    };
  }, [module, role, saveStatus, setModule, currentEvent]);

  return <VcmContext.Provider value={value}>{children}</VcmContext.Provider>;
}

export function useVcm(): VcmContextValue {
  const ctx = useContext(VcmContext);
  if (!ctx) throw new Error('useVcm must be used within a VcmProvider');
  return ctx;
}
